import { useState, useEffect, useRef } from 'react';
import { Fab } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatbotPanel from './ChatbotPanel';
import { useAuth } from '../../context/authContext';
import { getChatbotConfig } from '../../services/chatbotConfigService';

export default function ChatbotButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem('chatbotHidden') === 'true');
  const [tooltipVisible, setTooltipVisible] = useState(true);
  const [enabled, setEnabled] = useState(null);
  const tooltipTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getChatbotConfig();
        setEnabled(cfg.enabled);
      } catch {
        setEnabled(false);
      }
    })();
  }, []);

  useEffect(() => {
    tooltipTimer.current = setTimeout(() => setTooltipVisible(false), 10000);
    return () => clearTimeout(tooltipTimer.current);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'chatbotHidden') {
        setHidden(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    const customHandler = () => {
      setHidden(localStorage.getItem('chatbotHidden') === 'true');
    };
    window.addEventListener('chatbotVisibilityChange', customHandler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('chatbotVisibilityChange', customHandler);
    };
  }, []);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const onConfigUpdated = async () => {
      try {
        const cfg = await getChatbotConfig();
        setEnabled(cfg.enabled);
      } catch {
        setEnabled(false);
      }
    };
    window.addEventListener('chatbot-config-updated', onConfigUpdated);
    return () => window.removeEventListener('chatbot-config-updated', onConfigUpdated);
  }, []);

  // Si aún no cargó la config, no mostrar nada
  if (enabled === null) return null;

  // Si no hay usuario (login), solo mostrar si está habilitado
  if (!user) {
    if (!enabled) return null;
    // Si enabled es true, sigue al renderizado
  } else {
    // Hay usuario logueado
    // Si no está habilitado y el usuario no es admin, ocultar
    if (!enabled && !isAdmin) return null;
  }

  const handleToggle = () => {
    setOpen((prev) => !prev);
    setTooltipVisible(false);
    clearTimeout(tooltipTimer.current);
  };

  const handleHide = () => {
    localStorage.setItem('chatbotHidden', 'true');
    setHidden(true);
    clearTimeout(tooltipTimer.current);
  };

  const handleShow = () => {
    localStorage.removeItem('chatbotHidden');
    setHidden(false);
  };

  if (hidden) {
    return (
      <div
        onClick={handleShow}
        title="Mostrar chatbot"
        style={{
          position: 'fixed',
          bottom: 90,
          right: 20,
          zIndex: 1300,
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: '#E91E63',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          fontSize: 16,
          fontWeight: 700,
          opacity: 0.85,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.85'; }}
      >
        &lt;&lt;
      </div>
    );
  }

  return (
    <>
      {open && <ChatbotPanel onClose={() => setOpen(false)} />}
      {!open && tooltipVisible && (
        <div
          onClick={() => setTooltipVisible(false)}
          style={{
            position: 'fixed',
            bottom: 106,
            right: 92,
            zIndex: 1300,
            backgroundColor: '#fff',
            color: '#333',
            padding: '10px 18px',
            borderRadius: 16,
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
            whiteSpace: 'nowrap',
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              position: 'absolute',
              right: -6,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderLeft: '6px solid #fff',
              filter: 'drop-shadow(1px 0 1px rgba(0,0,0,0.08))',
            }}
          />
          <span>¿Te puedo ayudar en algo?</span>
          <button
            onClick={(e) => { e.stopPropagation(); setTooltipVisible(false); }}
            title="Cerrar"
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              fontSize: 16,
              cursor: 'pointer',
              padding: '0 2px',
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            &times;
          </button>
        </div>
      )}
      {!open && (
        <div style={{ position: 'fixed', bottom: 90, right: 20, zIndex: 1300 }}>
          <button
            onClick={handleHide}
            title="Ocultar chatbot"
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 22,
              height: 22,
              borderRadius: '50%',
              backgroundColor: '#fff',
              border: '2px solid #E91E63',
              color: '#E91E63',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1301,
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              padding: 0,
              lineHeight: 1,
            }}
          >
            &gt;&gt;
          </button>
          <Fab
            onClick={handleToggle}
            style={{
              backgroundColor: '#E91E63',
              color: '#fff',
              width: 64,
              height: 64,
            }}
            aria-label="Chatbot RDS"
          >
            <SmartToyIcon style={{ fontSize: 30 }} />
          </Fab>
        </div>
      )}
    </>
  );
}
